import { MILLI } from '@itc-mod/supply/shared';

// What a donation is worth on the materials board (plan 4B V1 and V2): FleetCom's
// calculate_pledge_value, in integer hundredths of a point rather than floats. Points
// aren't money. tests/value.test.ts pins these to what FleetCom's own function gives.

/** FleetCom's quality base: 500 is worth its list price. */
export const QUALITY_BASE = 500;

/** A material as pricing needs it. */
export interface PricedMaterial {
  id: string;
  name: string;
  /** Hundredths of a point, per SCU or per unit. */
  value: number;
  unit: 'scu' | 'units';
  hasQuality: boolean;
}

export interface PricedLine {
  name: string;
  material?: string | null | undefined;
  unit: string;
  crateSize: number | null;
  quantity: number;
  quality: number | null;
}

/** What a target adds to pricing. */
export interface TargetTerms {
  /** 100, or 150 for a corp build. */
  creditPercent: number;
  selfCredit: boolean;
  creator: string;
  /** The line that means one finished build, and what it's made of. */
  buildName: string;
  buildLines: readonly { material: string; quantity: number }[];
}

/** A line's amount in whole SCU or a count. */
export function wholeAmount(line: Pick<PricedLine, 'unit' | 'crateSize' | 'quantity'>): number {
  if (line.unit === 'crates') return line.quantity * (line.crateSize ?? 0);
  return line.unit === 'scu' ? line.quantity / MILLI : line.quantity;
}

/** A price by id, then by name ignoring case, as Sierra's lines name their material. */
export function materialOf(materials: readonly PricedMaterial[], line: Pick<PricedLine, 'name' | 'material'>): PricedMaterial | undefined {
  const wanted = line.name.trim().toLowerCase();
  return materials.find((material) => material.id === line.material) ?? materials.find((material) => material.name.trim().toLowerCase() === wanted);
}

/** One finished build: its recipe's lines at today's prices. */
export function buildValue(terms: Pick<TargetTerms, 'buildLines'>, materials: readonly PricedMaterial[]): number {
  return terms.buildLines.reduce((total, line) => {
    const material = materials.find((entry) => entry.id === line.material);
    return material === undefined ? total : total + material.value * wholeAmount({ unit: material.unit, crateSize: null, quantity: line.quantity });
  }, 0);
}

/** V1: a line's worth, in hundredths of a point. Unknown names are worth 0. */
export function lineValue(line: PricedLine, materials: readonly PricedMaterial[], terms: TargetTerms | null): number {
  if (terms !== null && line.name === terms.buildName) return buildValue(terms, materials) * line.quantity;
  const material = materialOf(materials, line);
  if (material === undefined) return 0;
  const quality = material.hasQuality && line.quality !== null && line.quality > 0 ? line.quality / QUALITY_BASE : 1;
  return material.value * wholeAmount(line) * quality;
}

export interface ValueOptions {
  groupBonusPercent: number;
  /** A receiver's own total, replacing the lines' (D17). */
  override?: number | null | undefined;
}

/**
 * V2: each donor's value, in order. The bonus applies only on a target, as Sierra's
 * donations have never had it. A creator's own share is 0 unless the target allows it.
 */
export function donorValues(donors: readonly { userId: string | null }[], lines: readonly PricedLine[], materials: readonly PricedMaterial[], terms: TargetTerms | null, options: ValueOptions): number[] {
  const count = donors.length;
  const override = options.override ?? null;
  const base = override ?? lines.reduce((total, line) => total + lineValue(line, materials, terms), 0);
  const bonus = terms !== null && count > 1 ? options.groupBonusPercent * count : 0;
  const multiplier = override !== null || terms === null ? 1 : (1 + bonus / 100) * (terms.creditPercent / 100);
  return donors.map((donor) => {
    if (terms !== null && donor.userId === terms.creator && !terms.selfCredit) return 0;
    return Math.round((base / count) * multiplier);
  });
}

/** `1,234.5 ⓤ`, from hundredths. */
export function pointsText(hundredths: number): string {
  return `${(hundredths / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })} ⓤ`;
}
